import React, { useState, useRef, useEffect, useCallback } from 'react';
import styles from './InsightPage.module.css';
import { ssoEnabled } from '../../auth/authConfig';
import { useAuth } from '../../hooks/useAuth';
import { useAlphaSenseToken } from '../../context/AlphaSenseTokenContext';
import { gensearchAuto } from '../../services/alphasenseService';
import Navbar from '../layout/Navbar';
import CompanySuggestionsPopup from '../common/CompanySuggestionsPopup';
import OrganisationResearchPopup from '../common/OrganisationResearchPopup';

/* Map a raw API document to a feed item */
// const mapDocument = (doc) => {
//   const companies = doc.companies?.map((c) => c.name).filter(Boolean).join(', ');
//   const meta = ['AlphaSense', companies, doc.releasedDay].filter(Boolean).join(' · ');
//   return { title: doc.title, meta };
// };

/* Convert a markdown section to safe HTML for display */
const sectionToHtml = (text = '') => {
  let t = text;

  // Strip table rows entirely (hard to render cleanly)
  t = t.replace(/^\|.+$/gm, '');

  // [[N • Source] ](url) → <sup><a>[N]</a></sup>  (citation superscript)
  t = t.replace(
    /\[\[(\d+)[^\]]*\]\s*\]\s*\(([^)]+)\)/g,
    '<sup><a href="$2" target="_blank" rel="noopener noreferrer" class="cite">[$1]</a></sup>'
  );

  // Remaining [text](url) → clickable link
  t = t.replace(
    /\[([^\]]+)\]\s*\(([^)]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer" class="mdlink">$1</a>'
  );

  // **bold**
  t = t.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');

  // ### sub-headings (appear inside a section)
  t = t.replace(/^#{3,4}\s+(.+)$/gm, '<h5 class="mdh5">$1</h5>');

  // > blockquote
  t = t.replace(/^>\s*(.+)$/gm, '<blockquote class="mdbq">$1</blockquote>');

  // - / * list items
  t = t.replace(/^[-*]\s+(.+)$/gm, '<li>$1</li>');
  // Wrap consecutive <li> blocks
  t = t.replace(/(<li>.*<\/li>\n?)+/g, (match) => `<ul class="mdul">${match}</ul>`);

  // Paragraphs: blank lines become paragraph breaks
  t = t
    .split(/\n{2,}/)
    .map((para) => para.trim())
    .filter(Boolean)
    .map((para) => (para.startsWith('<') ? para : `<p>${para.replace(/\n/g, ' ')}</p>`))
    .join('');

  return t;
};

/* Parse markdown response into result cards split by ## / ### sections */
const parseMarkdownToCards = (markdown = '') => {
  // Drop HTML citation block at the end
  const clean = markdown.split('<h3 class="citation-header">')[0];
  // Split on ## headings only (### handled inside sectionToHtml)
  const sections = clean.split(/^##\s+/m);

  const cards = [];

  sections.forEach((section, i) => {
    if (!section.trim()) return;

    if (i === 0) {
      const html = sectionToHtml(section);
      if (html) cards.push({ title: 'Overview', html, src: 'AlphaSense GenSearch' });
    } else {
      const newline = section.indexOf('\n');
      const title = (newline === -1 ? section : section.slice(0, newline)).trim();
      const body = newline === -1 ? '' : section.slice(newline + 1);
      const html = sectionToHtml(body);
      if (html) cards.push({ title, html, src: 'AlphaSense GenSearch' });
    }
  });

  return cards;
};

const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
};

const is401 = (err) => err?.message?.includes('401');

const getFirstName = (fullName = '') => {
  if (!fullName) return '';
  // Corporate format: "Last, First Middle" → take after comma
  if (fullName.includes(',')) return fullName.split(',')[1].trim().split(' ')[0];
  // Standard format: "First Last" → take first word
  return fullName.split(' ')[0];
};

/* ── Inner component — SSO enabled, has MSAL token ── */
const InsightPageInner = () => {
  const { getApiToken, account } = useAuth();
 const { alphasenseToken, feedItems, feedLoaded, feedError, agents, agentsLoading, agentsError } = useAlphaSenseToken();
  // const [feedItems, setFeedItems] = useState([]);
  // const [feedLoaded, setFeedLoaded] = useState(false);
  // const [feedError, setFeedError] = useState(null);

  // useEffect(() => {
  //   if (!isTokenReady) return;
  //   let cancelled = false;
  //   const loadFeed = async () => {
  //     try {
  //       const msalToken = await getApiToken();
  //       const data = await searchAlphaSenseDocuments('global capability centres', msalToken, alphasenseToken);
  //       if (!cancelled) {
  //         const items = (data?.data?.documents ?? []).map(mapDocument);
  //         setFeedItems(items);
  //         setFeedLoaded(true);
  //       }
  //     } catch (err) {
  //       if (!cancelled) {
  //         setFeedError(is401(err) ? '401 Unauthorized — AlphaSense token expired.' : 'Failed to load feed.');
  //         setFeedLoaded(true);
  //       }
  //     }
  //   };
  //   loadFeed();
  //   return () => { cancelled = true; };
  // }, [getApiToken, isTokenReady, alphasenseToken]);

  return (
    <InsightPageLayout
      feedLoaded={feedLoaded}
      feedItems={feedItems}
      feedError={feedError}
      getToken={getApiToken}
      alphasenseToken={alphasenseToken}
      userName={getFirstName(account?.name)}
      agents={agents}
      agentsLoading={agentsLoading}
      agentsError={agentsError}
    />
  );
};

/* ── No-SSO variant — no AlphaSense token available ── */
const InsightPageNoSSO = () => (
  <InsightPageLayout
    feedLoaded={true}
    feedItems={[]}
    feedError={null}
    getToken={async () => null}
    alphasenseToken={null}
    userName=""
    agents={[]}
    agentsLoading={false}
    agentsError={null}
  />
);

/* ── Shared UI layout ── */
const InsightPageLayout = ({ feedLoaded, feedItems, feedError, getToken, alphasenseToken, userName, agents, agentsLoading, agentsError }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [retryCountdown, setRetryCountdown] = useState(0);
  const [runningAgents, setRunningAgents] = useState({});
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [feedTooltip, setFeedTooltip] = useState(null); // { text, x, y }
  const hideTimerRef = useRef(null);
  const countdownRef = useRef(null);

  useEffect(() => () => clearInterval(countdownRef.current), []);

  const startCountdown = (seconds) => {
    setRetryCountdown(seconds);
    clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setRetryCountdown((prev) => {
        if (prev <= 1) { clearInterval(countdownRef.current); setSearchError(null); setResults(null); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const stripCitations = (text) =>
    text.replace(/\[\[[^\]]+\]\s*\]\s*\([^\s)]*\)?/g, '').replace(/\s{2,}/g, ' ').trim();

  const showTooltip = useCallback((e, text) => {
    if (!text) return;
    clearTimeout(hideTimerRef.current);
    const rect = e.currentTarget.getBoundingClientRect();
    setFeedTooltip({ text: stripCitations(text), x: rect.right + 10, y: rect.top });
  }, []);

  const scheduleHide = useCallback(() => {
    hideTimerRef.current = setTimeout(() => setFeedTooltip(null), 120);
  }, []);

  const cancelHide = useCallback(() => {
    clearTimeout(hideTimerRef.current);
  }, []);
  // const [agents, setAgents] = useState([]);
  // const [agentsLoading, setAgentsLoading] = useState(true);
  // const [agentsError, setAgentsError] = useState(null);

  // useEffect(() => {
  //   if (!alphasenseToken) { setAgentsLoading(false); return; }
  //   let cancelled = false;
  //   const loadAgents = async () => {
  //     try {
  //       const msalToken = await getToken();
  //       const data = await listGensearchAgents(msalToken, alphasenseToken);
  //       if (!cancelled) { setAgents(data?.agents ?? []); setAgentsLoading(false); }
  //     } catch (err) {
  //       if (!cancelled) {
  //         setAgentsError(is401(err) ? '401 Unauthorized — AlphaSense token expired.' : 'Failed to load agents.');
  //         setAgentsLoading(false);
  //       }
  //     }
  //   };
  //   loadAgents();
  //   return () => { cancelled = true; };
  // }, [getToken, alphasenseToken]);
  const [feedCollapsed, setFeedCollapsed] = useState(false);

  const doSearch = async (q) => {
    if (!alphasenseToken) { setSearchError('AlphaSense token not ready — please wait a moment.'); return; }
    setSearching(true);
    setResults(null);
    setSearchError(null);
    clearInterval(countdownRef.current);
    setRetryCountdown(0);
    try {
      const msalToken = await getToken();
      const data = await gensearchAuto(q, msalToken, alphasenseToken);
      setResults(parseMarkdownToCards(data?.markdown ?? ''));
    } catch (err) {
      let message;
      if (err?.code === 'CREDIT_LIMIT_EXHAUSTED') {
        message = err.message;
        if (err.retryAfterSeconds) startCountdown(err.retryAfterSeconds);
      } else if (is401(err)) {
        message = '401 Unauthorized — AlphaSense token expired.';
      } else {
        message = 'Search failed. Please try again.';
      }
      setSearchError(message);
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleSearch = () => { if (query.trim()) doSearch(query.trim()); };
  const handleKeyDown = (e) => { if (e.key === 'Enter' && query.trim()) doSearch(query.trim()); };
  const clearResults = () => { setResults(null); setQuery(''); setSearchError(null); clearInterval(countdownRef.current); setRetryCountdown(0); };

  const runAgent = (id) => {
    if (runningAgents[id]) return;
    setRunningAgents((prev) => ({ ...prev, [id]: true }));
    setTimeout(() => setRunningAgents((prev) => ({ ...prev, [id]: false })), 2000);
  };

  return (
    <>
    <div className={styles.wrap}>
      <Navbar />
      <div className={`${styles.layout} ${feedCollapsed ? styles.layoutCollapsed : ''}`}>

        {/* Left: Intelligence Feed */}
        <div className={`${styles.left} ${feedCollapsed ? styles.leftCollapsed : ''}`}>
          <div className={styles.leftHead}>
            {!feedCollapsed && <span className={styles.leftHeadLabel}>Latest GCC intelligence</span>}
            <div className={styles.leftHeadControls}>
              {!feedCollapsed && <div className={styles.liveDot} title="Auto-refreshing" />}
              <button
                className={styles.collapseBtn}
                onClick={() => setFeedCollapsed((v) => !v)}
                title={feedCollapsed ? 'Expand feed' : 'Collapse feed'}
                aria-label={feedCollapsed ? 'Expand intelligence feed' : 'Collapse intelligence feed'}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path
                    d={feedCollapsed ? 'M5 3l4 4-4 4' : 'M9 3L5 7l4 4'}
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
          {!feedCollapsed && (
            <div className={styles.feed}>
              {!feedLoaded ? (
                <div className={styles.pulseRow}>
                  <div className={styles.dot} />
                  Fetching signals via AlphaSense…
                </div>
              ) : feedError ? (
                <div className={styles.errorRow}>
                  <span className={styles.errorIcon}>⚠</span>{feedError}
                </div>
              ) : feedItems.length === 0 ? (
                <div className={styles.pulseRow}>No signals available.</div>
              ) : (
                feedItems.map((item, i) => (
                  <div
                    key={i}
                    className={styles.ic}
                    onClick={() => item.url && window.open(item.url, '_blank', 'noopener,noreferrer')}
                    style={item.url ? { cursor: 'pointer' } : undefined}
                    onMouseEnter={item.summary ? (e) => showTooltip(e, item.summary) : undefined}
                    onMouseLeave={item.summary ? scheduleHide : undefined}
                  >
                    <div className={styles.ititle}>{item.title}</div>
                    <div className={styles.imeta}>{item.meta}</div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Right: Search + Agents */}
        <div className={styles.right}>
          {/* Fixed top — greeting + search box never scroll */}
          <div className={styles.rightFixed}>
            <div className={styles.greeting}>
              <h1>{getGreeting()}{userName ? `, ${userName}` : ''}</h1>
              <p>Search any company, market, or GCC signal — or run an agent for deeper analysis.</p>
            </div>

            <div className={styles.searchBox}>
              <input
                className={styles.sinput}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask AlphaSense… e.g. Boston Scientific India, captive buyouts 2025"
              />
              <button className={styles.go} onClick={handleSearch}>Search</button>
            </div>
          </div>

          {/* Scrollable area — results + agents */}
          <div className={styles.rightScroll}>
          {(results !== null || searching) && (
            <div className={styles.resultsSection}>
              <div className={styles.resHead}>
                <span className={styles.resLabel}>
                  {searching ? 'Querying AlphaSense…' : `Results for "${query}"`}
                </span>
                <div className={styles.resActions}>
                  <button
                    className={styles.back}
                    onClick={() => setFeedCollapsed((v) => !v)}
                    title={feedCollapsed ? 'Show sidebar' : 'Hide sidebar'}
                  >
                    <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                      {feedCollapsed ? (
                        <>
                          <path d="M8 1h4v4M5 12H1V8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M12 1L7.5 5.5M1 12l4.5-4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </>
                      ) : (
                        <>
                          <path d="M1 5V1h4M8 12h4V8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M1 1l4.5 4.5M12 12L7.5 7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </>
                      )}
                    </svg>
                  </button>
                  <button className={styles.back} onClick={clearResults}>Clear</button>
                </div>
              </div>
              {searching ? (
                <div className={styles.pulseRow}>
                  <div className={styles.dot} />
                  Results may take ~30 seconds to load.
                </div>
              ) : searchError ? (
                <div className={styles.errorRow}>
                  <span className={styles.errorIcon}>⚠</span>
                  {retryCountdown > 0 ? `Credit limit exceeded — retry in ${retryCountdown}s…` : searchError}
                </div>
              ) : results.length === 0 ? (
                <div className={styles.pulseRow}>No results found.</div>
              ) : (
                results.map((r, i) => (
                  <div key={i} className={styles.rc}>
                    <div className={styles.rcTitle}>{r.title}</div>
                    <div className={styles.rcSrc}>{r.src}</div>
                    <div
                      className={styles.rcBody}
                      dangerouslySetInnerHTML={{ __html: r.html }}
                    />
                  </div>
                ))
              )}
            </div>
          )}

          <div className={styles.secLabel}>Agents</div>
          <p className={styles.secSubtitle}>Your report will be ready in approximately 10 minutes and sent to your email.</p>
          {agentsError ? (
            <div className={styles.errorRow}>
              <span className={styles.errorIcon}>⚠</span>{agentsError}
            </div>
          ) : (
          <div className={styles.agentGrid}>
            {agentsLoading ? (
              [1, 2, 3].map((n) => (
                <div key={n} className={`${styles.ac} ${styles.acSkeleton}`}>
                  <div className={styles.skLine} style={{ width: '70%' }} />
                  <div className={styles.skLine} style={{ width: '90%', marginTop: 6 }} />
                  <div className={styles.skLine} style={{ width: '55%', marginTop: 4 }} />
                </div>
              ))
            ) : agents.map((agent) => {
              const isRunning = !!runningAgents[agent.id];
              const isDisabled = false;
              return (
                <div
                  key={agent.id}
                  className={`${styles.ac} ${isRunning ? styles.running : ''} ${isDisabled ? styles.acDisabled : ''}`}
                  aria-disabled={isDisabled}
                  style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
                  onClick={isDisabled ? undefined : () => setSelectedAgent(agent)}
                >
                  <div className={styles.acTitle}>{agent.name}</div>
                  <div className={styles.acDesc}>
                    {isDisabled ? 'Coming Soon.' : agent.executionMode?.replace(/_/g, ' ').toLowerCase()}
                  </div>
                </div>
              );
            })}
            <div className={`${styles.ac} ${styles.acComingSoon}`}>
              <div className={styles.acTitle}>New agents coming soon.</div>
            </div>
          </div>
          )}
          </div>{/* end rightScroll */}
        </div>

      </div>
    </div>

      <CompanySuggestionsPopup
        isOpen={!!selectedAgent && selectedAgent.workflowType !== 'ORGANISATION'}
        onClose={() => setSelectedAgent(null)}
        agent={selectedAgent}
      />
      <OrganisationResearchPopup
        isOpen={!!selectedAgent && selectedAgent.workflowType === 'ORGANISATION'}
        onClose={() => setSelectedAgent(null)}
        agent={selectedAgent}
      />

      {feedTooltip && (
        <div
          className={styles.feedTooltip}
          style={{ top: feedTooltip.y, left: feedTooltip.x }}
          onMouseEnter={cancelHide}
          onMouseLeave={scheduleHide}
        >
          {feedTooltip.text}
        </div>
      )}
    </>
  );

};

/* ── Root export ── */
const InsightPage = () =>
  ssoEnabled
    ? <InsightPageInner />
    : <InsightPageNoSSO />;

export default InsightPage;
